import React, { useEffect, useState } from "react";
import { X, Download, Trash2, Film } from "lucide-react";
import { toast } from "sonner";
import { lockBodyScroll } from "@/lib/lockBodyScroll";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Pop-up that lists every completed Kling video shot for a single photo.
 * Each entry shows the video in an HTML5 <video controls> player + download +
 * delete buttons. Admin can play and download multiple shots in one place.
 *
 * Props:
 *   - open           : boolean
 *   - onClose        : () => void
 *   - photoIndex     : number    (1-based label used in filename)
 *   - jobs           : VideoShot[]
 *   - addressLabel   : string    (used in download filename)
 *   - onDelete       : (jobId) => Promise<void>   parent removes the job from its state
 */
export default function VideoShotGalleryModal({
    open,
    onClose,
    photoIndex,
    jobs = [],
    addressLabel = "",
    onDelete,
}) {
    // Two-click delete confirm — first click arms the button, second click actually deletes.
    // Avoids window.confirm() which silently fails in some modal contexts (Safari iOS, etc.)
    const [armedDelete, setArmedDelete] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", handler);
        const release = lockBodyScroll(true);
        return () => {
            window.removeEventListener("keydown", handler);
            release();
        };
    }, [open, onClose]);

    useEffect(() => {
        if (!armedDelete) return;
        const t = setTimeout(() => setArmedDelete(null), 4000);
        return () => clearTimeout(t);
    }, [armedDelete]);

    if (!open) return null;

    const slugify = (s) => (s || "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^\w\s.-]/g, "")
        .replace(/\s+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 60) || "foto";

    const downloadName = (job) => {
        const addr = slugify(addressLabel);
        const n = (photoIndex ?? 0) >= 0 ? String((photoIndex ?? 0) + 1).padStart(2, "0") : "XX";
        const preset = (job.preset_key || "shot").replace(/_/g, "-");
        return `${addr ? addr + " - " : ""}foto ${n} - ${preset} - SocialFactoryAI.mp4`;
    };

    const handleDelete = async (jobId) => {
        if (armedDelete !== jobId) {
            setArmedDelete(jobId);
            return;
        }
        setArmedDelete(null);
        setDeletingId(jobId);
        try {
            await onDelete?.(jobId);
            toast.success("Videoshot verwijderd");
        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || "Verwijderen mislukt");
        } finally {
            setDeletingId(null);
        }
    };

    const completed = jobs.filter((j) => j.status === "completed" && j.result_url);

    return (
        <div
            className="fixed inset-0 z-[210] bg-black/90 flex items-start justify-center overflow-y-auto p-4 lg:p-8"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            data-testid="video-shot-gallery-modal"
        >
            <div className="w-full max-w-4xl bg-[#FFFFFF] border border-sky-500/30 rounded-md shadow-2xl shadow-black/60">
                {/* Header */}
                <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-200 bg-[#FFFFFF]/95 backdrop-blur-md">
                    <div className="flex items-center gap-2 min-w-0">
                        <Film className="h-4 w-4 text-sky-700 flex-shrink-0" />
                        <h3 className="font-display font-bold text-base tracking-tight truncate">
                            {(photoIndex ?? 0) >= 0 ? `Videoshots voor foto #${(photoIndex ?? 0) + 1}` : "Losgekoppelde videoshots"}
                        </h3>
                        <span className="text-[10px] uppercase tracking-wider text-sky-700 bg-sky-500/10 border border-sky-500/30 rounded-sm px-1.5 py-0.5 flex-shrink-0">
                            {completed.length}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center justify-center h-9 w-9 bg-zinc-100 hover:bg-zinc-200 text-zinc-950 rounded-sm transition"
                        data-testid="video-shot-gallery-close"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 space-y-5">
                    {completed.length === 0 && (
                        <div className="text-sm text-zinc-500 text-center py-12">
                            Nog geen voltooide videoshots voor deze foto.
                        </div>
                    )}

                    {completed.map((job) => (
                        <div
                            key={job.id}
                            className="bg-[#FAFAFA] border border-zinc-200 rounded-sm p-3 space-y-2"
                            data-testid={`video-shot-gallery-item-${job.id}`}
                        >
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                                <div className="min-w-0">
                                    <div className="text-sm font-bold text-zinc-800 truncate">
                                        {job.preset_label_nl || job.preset_key}
                                    </div>
                                    <div className="text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
                                        <span>{new Date(job.created_at).toLocaleString("nl-BE")}</span>
                                        {job.duration_ms != null && (
                                            <span className="font-mono">{(job.duration_ms / 1000).toFixed(1)}s</span>
                                        )}
                                        {job.cost_eur != null && (
                                            <span className="font-mono">€{Number(job.cost_eur).toFixed(2)}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <a
                                        href={`${BACKEND}${job.result_url}?dl=${encodeURIComponent(downloadName(job))}`}
                                        download={downloadName(job)}
                                        className="inline-flex items-center gap-1.5 text-xs px-3 h-8 bg-sky-500 hover:bg-sky-400 text-black font-bold rounded-sm transition active:scale-95"
                                        data-testid={`video-shot-gallery-download-${job.id}`}
                                    >
                                        <Download className="h-3.5 w-3.5" /> Download MP4
                                    </a>
                                    {onDelete && (
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(job.id)}
                                            disabled={deletingId === job.id}
                                            className={`inline-flex items-center gap-1.5 text-xs px-3 h-8 rounded-sm transition active:scale-95 border ${armedDelete === job.id ? "bg-red-500 hover:bg-red-400 text-zinc-950 border-red-400 font-bold" : "bg-zinc-100 hover:bg-red-500/15 text-red-700 border-red-500/20"} disabled:opacity-60 disabled:cursor-not-allowed`}
                                            data-testid={`video-shot-gallery-delete-${job.id}`}
                                            title={armedDelete === job.id ? "Klik nogmaals om definitief te verwijderen" : "Verwijderen"}
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                            {deletingId === job.id ? "Bezig…" : armedDelete === job.id ? "Klik nogmaals" : "Verwijder"}
                                        </button>
                                    )}
                                </div>
                            </div>
                            <video
                                src={`${BACKEND}${job.result_url}`}
                                controls
                                playsInline
                                preload="metadata"
                                className="w-full max-h-[60vh] rounded-sm bg-zinc-50"
                                data-testid={`video-shot-gallery-player-${job.id}`}
                            />
                            {job.optimized_prompt && (
                                <details className="text-[11px] text-zinc-500">
                                    <summary className="cursor-pointer text-zinc-500 hover:text-zinc-600 select-none">Prompt</summary>
                                    <div className="mt-1 font-mono leading-snug bg-black/40 border border-white/5 rounded-sm p-2">
                                        {job.optimized_prompt}
                                    </div>
                                </details>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
